import React, { createContext, ReactChild, useCallback, useContext, useMemo, useRef } from 'react';
import { formatTable } from 'roosterjs-editor-api';
import { TableFormat } from 'roosterjs-editor-types';
import { useToolbarActionContext } from '../../../context';
import { PREDEFINED_TABLE_STYLES } from './common';

interface ITableFormatContext {
    getTableFormat(): TableFormat;
    setTableFormat(format: TableFormat): void;
}

const defaultFunction = () => PREDEFINED_TABLE_STYLES.get('default')!;

const TableFormatContext = createContext<ITableFormatContext>({ getTableFormat: defaultFunction, setTableFormat: defaultFunction });

export function TableFormatStore({ children }: { children?: ReactChild | ReactChild[] }) {
    const formatInfoRef = useRef(new WeakMap<HTMLElement, TableFormat>());
    const { editor } = useToolbarActionContext();

    const getTableFormat = useCallback(() => {
        const table = editor.getElementAtCursor('TABLE');
        if (!table) {
            return PREDEFINED_TABLE_STYLES.get('default')!;
        }
        const { current: formatInfo } = formatInfoRef;
        let format = formatInfo.get(table);
        if (format) {
            return format;
        }
        try {
            format = JSON.parse(table.dataset['tableFormat'] ?? '');
        } catch (err) {
            format = PREDEFINED_TABLE_STYLES.get('default')!;
        }
        formatInfo.set(table, format as TableFormat);
        return format as TableFormat;
    }, [editor]);

    const setTableFormat = useCallback((format: TableFormat) => {
        const table = editor.getElementAtCursor('TABLE');
        if (!table) {
            return;
        }
        const { current: formatInfo } = formatInfoRef;
        table.dataset['tableFormat'] = JSON.stringify(format);
        formatTable(editor, format, table as HTMLTableElement);
        formatInfo.set(table, format);
    }, [editor]);

    const contextValue = useMemo((): ITableFormatContext => ({ getTableFormat, setTableFormat }), [getTableFormat, setTableFormat]);

    return (
        <TableFormatContext.Provider value={contextValue}>
            {children}
        </ TableFormatContext.Provider>
    );
}

export const useTableFormat = () => useContext(TableFormatContext);
